import React, { useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import 'moment/locale/es'
import { useStateValue } from 'context/store'
import MaterialIcon from '../materialIcon/materialIcon'
import { patchToApi, getBlobFromApi } from 'actions'
import pdfIcon from 'images/pdf-icon.png'
import Utils from 'utils/utils'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import {
   makeStyles,
   Grid,
   Typography,
   Collapse,
   Divider,
   Paper
} from '@material-ui/core'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import { ExpandLess, ExpandMore, AccountCircle } from '@material-ui/icons'

const useRegulationCardStyles = makeStyles(theme => ({
   cardContainer: {
      marginBottom: theme.spacing(1)
   },
   clickable: {
      cursor: 'pointer'
   },
   cardHeader: {
      backgroundColor: '#EBEFF8',
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(1)
   },
   cardHeaderSide: {
      display: 'flex'
   },
   cardHeaderTitleAndIconContainer: {
      display: 'flex',
      alignItems: 'center'
   },
   cardHeaderTitle: {
      fontSize: 10,
      color: '#8A8FAB'
   },
   cardHeaderTitleAndIcon: {
      marginLeft: theme.spacing(0.5)
   },
   cardHeaderPriority: {
      paddingLeft: theme.spacing(1)
   },
   high: {
      color: '#F01E46'
   },
   medium: {
      color: '#F08E1E'
   },
   low: {
      color: '#60DC32'
   },
   dot: {
      height: 6,
      width: 6,
      marginLeft: theme.spacing(1),
      marginTop: 2,
      borderRadius: '50%',
      display: 'inline-block'
   },
   read: {
      backgroundColor: '#3264DC'
   },
   unread: {
      backgroundColor: '#60DC32'
   },
   cardBody: {
      display: 'flex',
      padding: theme.spacing(1.5)
   },
   cardBodyIconContainer: {
      color: '#3264DC',
      marginRight: theme.spacing(1.5)
   },
   cardHeaderPriorityIconContainer: {
      fontSize: 12
   },
   cardBodyCommentContainer: {
      flex: '1 1 auto'
   },
   cardBodyComment: {
      textAlign: 'left',
      fontSize: 12,
      color: '#8A8FAB',
      fontWeight: 'normal'
   },
   sender: {
      textAlign: 'left',
      fontSize: 12,
      margin: theme.spacing(0.3, 0, 0.5)
   },
   collapseContainer: {
      height: 40
   },
   blueLabel: {
      color: '#3264DC',
      fontWeight: 'bold'
   },
   attachmentContainer: {
      display: 'flex',
      marginTop: theme.spacing(0.5),
      fontSize: 12,
      fontWeight: 'normal'
   }
}))

function RegulationCard({ regulation, isCollapsable = false }) {
   const classes = useRegulationCardStyles()

   const [{ regulationsSummary }, dispatch] = useStateValue()

   const {
      id,
      code,
      sender,
      subject,
      attachment,
      subdomain,
      hour,
      createdDate,
      read,
      comment,
      priority
   } = regulation
   const { name: subdomainName, icon: subdomainIcon } = subdomain

   const [isOpen, setIsOpen] = useState(false)
   const [isRead, setIsRead] = useState(read)

   async function _downloadAttachment() {
      const payload = { query: code, folder: 'regulations/' }
      const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/files/download`
      const bytes = await getBlobFromApi(url, payload)
      Utils.saveByteArray(attachment, 'application/pdf', bytes)
   }

   const downloadAttachment = useApiRequest(
      _downloadAttachment,
      'Normativas Card: Descargar'
   )

   async function _updateReadStatus(regulationId) {
      const payload = { read: true }
      const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/circulars-regulations/${regulationId}`
      await patchToApi(url, payload)
      setIsRead(true)
      dispatch({
         type: 'SET_REGULATION_READ',
         regulationsSummary,
         regulationId
      })
   }

   const updateReadStatus = useApiRequest(
      _updateReadStatus,
      'Normativas Card: Marcar Leída',
      false
   )

   const localLocale = moment(
      `${createdDate} ${hour}`,
      'DD-MM-YYYY hh:mm:ss'
   ).locale('es')
   const prettyCreatedDate = localLocale.fromNow()
   const formattedHour = localLocale.format('h:mm a')

   function toggleOpen() {
      if (!isOpen && !isRead) {
         updateReadStatus(id)
      }
      setIsOpen(previous => !previous)
   }

   function handleClick() {
      if (!isCollapsable && !isRead) {
         updateReadStatus(id)
      }
   }

   const Component = isCollapsable ? Grid : Paper

   return (
      <Component
         className={clsx({
            [classes.cardContainer]: !isCollapsable,
            [classes.clickable]: !isRead && !isCollapsable
         })}
         onClick={handleClick}
      >
         <Grid className={classes.cardHeader}>
            <Grid className={classes.cardHeaderSide}>
               <Grid>
                  <Grid className={classes.cardHeaderTitleAndIconContainer}>
                     <MaterialIcon
                        fill="outlined"
                        name={subdomainIcon}
                        size={12}
                     />
                     <Typography
                        className={`${classes.cardHeaderTitle} ${classes.cardHeaderTitleAndIcon}`}
                     >
                        {subdomainName}
                     </Typography>
                  </Grid>
                  <Typography className={classes.cardHeaderTitle}>
                     {prettyCreatedDate}
                  </Typography>
               </Grid>
               {priority && (
                  <Grid
                     className={`${classes[priority.toLowerCase()]} ${
                        classes.cardHeaderPriority
                     }`}
                  >
                     <PriorityHighIcon fontSize="inherit" />
                  </Grid>
               )}
            </Grid>
            <Grid className={classes.cardHeaderSide}>
               <Typography className={classes.cardHeaderTitle}>
                  {formattedHour}
               </Typography>
               <span
                  className={clsx(classes.dot, {
                     [classes.read]: isRead,
                     [classes.unread]: !isRead
                  })}
               />
            </Grid>
         </Grid>
         {isCollapsable ? (
            <CollapsableCardBody
               comment={comment}
               sender={sender}
               subject={subject}
               attachment={attachment}
               downloadAttachment={downloadAttachment}
               isOpen={isOpen}
               toggleOpen={toggleOpen}
               isRead={isRead}
            />
         ) : (
            <CardBody
               comment={comment}
               sender={sender}
               subject={subject}
               attachment={attachment}
               downloadAttachment={downloadAttachment}
            />
         )}
      </Component>
   )
}

export default RegulationCard

// -----------------------

function CollapsableCardBody({
   comment,
   sender,
   subject,
   attachment,
   isOpen,
   isRead,
   toggleOpen,
   downloadAttachment
}) {
   const classes = useRegulationCardStyles()

   return (
      <Collapse in={isOpen} collapsedSize={75}>
         <Grid
            className={clsx(classes.cardBody, {
               [classes.clickable]: !isRead
            })}
            onClick={toggleOpen}
         >
            <Grid className={classes.cardBodyIconContainer}>
               <AccountCircle fontSize="small" />
            </Grid>
            <Grid className={classes.cardBodyCommentContainer}>
               <Typography className={classes.sender}>{sender}</Typography>
               <div className={classes.cardBodyComment}>
                  <span className={classes.blueLabel}>Asunto: </span>
                  {subject}
               </div>
               <div className={classes.cardBodyComment}>
                  <span className={classes.blueLabel}>Comentario: </span>
                  {comment}
               </div>
               <Grid
                  className={`${classes.attachmentContainer} ${classes.clickable}`}
                  onClick={downloadAttachment}
               >
                  <img src={pdfIcon} alt="archivo pdf" />
                  {attachment}
               </Grid>
            </Grid>
            <Grid>{isOpen ? <ExpandLess /> : <ExpandMore />}</Grid>
         </Grid>
         <Divider variant="middle" />
      </Collapse>
   )
}

// -----------------------

function CardBody({
   comment,
   sender,
   subject,
   attachment,
   downloadAttachment
}) {
   const classes = useRegulationCardStyles()

   return (
      <Grid className={classes.cardBody}>
         <Grid className={classes.cardBodyIconContainer}>
            <AccountCircle fontSize="small" />
         </Grid>
         <Grid className={classes.cardBodyCommentContainer}>
            <Typography className={classes.sender}>{sender}</Typography>
            <div className={classes.cardBodyComment}>
               <span className={classes.blueLabel}>Asunto: </span>
               {subject}
            </div>
            <div className={classes.cardBodyComment}>
               <span className={classes.blueLabel}>Comentario: </span>
               {comment}
            </div>
            <Grid
               className={`${classes.attachmentContainer} ${classes.clickable}`}
               onClick={downloadAttachment}
            >
               <img src={pdfIcon} alt="archivo pdf" />
               {attachment}
            </Grid>
         </Grid>
      </Grid>
   )
}
